import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { Box, theme, Heading, Button, Image, Text } from '@everlywell/ui-kit';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import renderRichText, {
  ContentfulRichText,
} from 'utils/contentful/renderRichText';
import { replaceRedirectionLinkRoot } from 'utils/helpers/replaceRedirectionLinkRoot';
import { ImageType } from 'utils/types';
import { CallToAction } from 'utils/types/contentfulTypes';

export type PromotionBannerV2Props = {
  description?: ContentfulRichText;
  bgColor?: string;
  headingColor?: string;
  textColor?: string;
  cta?: CallToAction;
  desktopBackgroundImage?: ImageType;
  desktopBackgroundImageAltText?: string;
  mobileBackgroundImage?: ImageType;
  mobileBackgroundImageAltText?: string;
};

const getRichTextOptions = (
  headingColor: string | undefined,
  textColor: string | undefined,
) => {
  const options: Options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <Text
          fontSize={['lg', null, '2xl']}
          fontWeight={theme.fontWeights.normal}
          marginBottom={[3, null, 6]}
          color={textColor || undefined}
        >
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_2]: (_, children) => (
        <Heading
          as="h2"
          fontSize={['3xl', null, '8xl']}
          fontWeight="normal"
          lineHeight={['20px', null, '40px']}
          marginBottom={4}
          color={headingColor || 'moonlight.base'}
        >
          {children}
        </Heading>
      ),
      [BLOCKS.HEADING_3]: (_, children) => (
        <Heading
          as="h3"
          fontSize={['xl', null, '2xl']}
          fontWeight="medium"
          lineHeight={['27px', null, '28px']}
          marginBottom={4}
          color={headingColor || undefined}
        >
          {children}
        </Heading>
      ),
      [BLOCKS.UL_LIST]: (_, children) => (
        <ul
          style={{
            fontSize: theme.fontSizes['lg'],
            listStyleType: 'initial',
            marginInlineStart: '1em',
          }}
        >
          <li style={{ listStyle: 'disc' }}>{children}</li>
        </ul>
      ),
      [BLOCKS.OL_LIST]: (_, children) => (
        <ol
          style={{
            fontSize: theme.fontSizes['lg'],
            listStyleType: 'initial',
            marginInlineStart: '1em',
          }}
        >
          <li style={{ listStyle: 'numeric' }}>{children}</li>
        </ol>
      ),
    },
  };

  return options;
};

/**
 * PromotionBannerV2 lets us display information with the shape background, description, and cta.
 */
const PromotionBannerV2 = (props: PromotionBannerV2Props) => {
  const {
    bgColor,
    headingColor,
    textColor,
    cta,
    desktopBackgroundImage,
    desktopBackgroundImageAltText,
    mobileBackgroundImage,
    mobileBackgroundImageAltText,
    description,
  } = props;

  const isCtaFullUrl = cta?.url?.includes('http');
  const onCtaClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        component: 'PromotionBannerV2',
        ctaText: cta?.label ?? '',
        ctaUrl: cta?.url ?? '',
      },
    });
  };

  const options = getRichTextOptions(headingColor, textColor);

  return (
    <SectionContainer
      centerContent
      size={['sm', 'sm', 'sm', 'xl']}
      marginTop={10}
    >
      <Box
        background={bgColor}
        display={['flex']}
        flexFlow={['column', null, 'row']}
        height={['auto', null, '400px']}
        overflow="hidden"
        position={['unset', null, 'relative']}
        borderRadius={12}
        width="100%"
      >
        <Box
          flex={['unset', null, 1]}
          order={['2', null, '1']}
          display="flex"
          flexFlow="column"
          alignItems="center"
          zIndex={['initial', null, '1']}
          justifyContent={'center'}
          textAlign={['left', null, 'center']}
          padding={['6', null, '12']}
        >
          {description ? (
            <Box marginTop={['6', null, 'unset']}>
              <Box>{renderRichText(description, options)}</Box>
              {cta?.label && cta?.url && (
                <>
                  {isCtaFullUrl ? (
                    <Button
                      variant="primary"
                      colorScheme="whiteCS"
                      as="a"
                      href={replaceRedirectionLinkRoot(cta.url)}
                      onClick={() => onCtaClick()}
                    >
                      {cta.label}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      colorScheme="whiteCS"
                      as="a"
                      href={cta.url}
                      onClick={onCtaClick}
                    >
                      {cta.label}
                    </Button>
                  )}
                </>
              )}
            </Box>
          ) : null}
        </Box>

        <Box
          flex={['unset', null, 1.5]}
          display={['none', null, 'flex']}
          alignItems={['center', 'flex-start', 'center']}
          justifyContent="center"
          order={['1', null, '2']}
          height={['300px', null, 'auto']}
          overflow={['unset', null, 'hidden']}
          position="relative"
        >
          {desktopBackgroundImage ? (
            <Image
              src={desktopBackgroundImage.gatsbyImageData.images.fallback?.src}
              alt={desktopBackgroundImageAltText || ''}
              boxSize="100%"
              objectFit="cover"
              clipPath={'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 24% 40%)'}
            />
          ) : null}
        </Box>
        <Box
          flex={['unset', null, 1]}
          display={['flex', null, 'none']}
          alignItems={['center', 'flex-start', 'center']}
          justifyContent="center"
          order={['1', null, '2']}
          height={['300px', null, 'auto']}
          overflow={['unset', null, 'hidden']}
          position="relative"
        >
          {mobileBackgroundImage ? (
            <Image
              src={mobileBackgroundImage.gatsbyImageData.images.fallback?.src}
              alt={mobileBackgroundImageAltText || ''}
              boxSize="100%"
              objectFit="cover"
              clipPath={'ellipse(80% 40% at 50% 40%)'}
              transform={'scale(1.4)'}
            />
          ) : null}
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default PromotionBannerV2;
